/**
 * First-touch signup attribution fallback for direct-to-app traffic.
 *
 * The marketing site (dunea.ai) sets the same `dunea_attr` cookie on its own
 * first touch; this covers visitors who land straight on app.dunea.ai (paid
 * links, docs, word of mouth). First-touch wins: an existing cookie is never
 * overwritten. The studio API reads the cookie server-side at signup — see
 * apps/studio/src/modules/auth/attribution.ts for the parse/whitelist side.
 */

import { setDraft } from '~/lib/persistence/chat-draft';

const COOKIE_NAME = 'dunea_attr';
const MAX_AGE_SECONDS = 90 * 24 * 3600;
const MAX_VALUE_LENGTH = 512;
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'] as const;
const PROMPT_PARAM = 'prompt';

/** Read and parse the existing attribution cookie, or null if absent/unusable. */
function readAttributionCookie(): Record<string, string> | null {
  const match = new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`).exec(document.cookie);

  if (!match) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as unknown;

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as Record<string, string>;
  } catch {
    // Malformed cookie — treat as absent so a fresh first touch is recorded.
    return null;
  }
}

function writeAttributionCookie(attribution: Record<string, string>): void {
  let cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(attribution))}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;

  // Share with the studio API host. On localhost / previews, a host-only
  // cookie still reaches the API because it's same-origin via the dev proxy.
  if (/(^|\.)dunea\.ai$/.test(window.location.hostname)) {
    cookie += '; Domain=.dunea.ai; Secure';
  }

  document.cookie = cookie;
}

/**
 * Mark a signup that starts on the desktop app's sign-in page, so the team's
 * Slack signup line can say it came from Dunea Desktop. Short-lived and
 * separate from the first-touch cookie, which must never be overwritten.
 */
export function markDesktopSignup(): void {
  try {
    let cookie = 'dunea_signup_platform=desktop; Max-Age=3600; Path=/; SameSite=Lax';

    if (/(^|\.)dunea\.ai$/.test(window.location.hostname)) {
      cookie += '; Domain=.dunea.ai; Secure';
    }

    document.cookie = cookie;
  } catch {
    // Attribution must never break sign-in.
  }
}

export function captureFirstTouchAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const existing = readAttributionCookie();

    if (existing) {
      // First touch is already recorded and must not be overwritten. The one
      // exception is the hero prompt: visitors coming from the marketing site
      // always have a cookie by the time they reach the app (it's set on
      // dunea.ai with Domain=.dunea.ai), so without this merge `initial_prompt`
      // would only ever land for direct-to-app traffic — i.e. never for the
      // funnel it was built for.
      const carried = params.get(PROMPT_PARAM)?.trim();

      if (carried && !existing.initial_prompt) {
        existing.initial_prompt = carried.slice(0, MAX_VALUE_LENGTH);
        writeAttributionCookie(existing);
      }

      return;
    }

    let referrer = document.referrer || '';

    // Same-product referrals (marketing site, previews) are not attribution.
    if (referrer && /(^|\.)dunea\.(ai|app)$/.test(new URL(referrer).hostname)) {
      referrer = '';
    }

    const attribution: Record<string, string> = {
      captured_at: new Date().toISOString(),
      landing_page: window.location.pathname,
    };

    if (referrer) {
      attribution.referrer = referrer.slice(0, MAX_VALUE_LENGTH);
    }

    for (const key of UTM_KEYS) {
      const value = params.get(`utm_${key}`);

      if (value) {
        attribution[`utm_${key}`] = value.slice(0, MAX_VALUE_LENGTH);
      }
    }

    // The idea typed into the marketing-site hero rides along so signup
    // records what the visitor wanted to build.
    const prompt = params.get(PROMPT_PARAM)?.trim();

    if (prompt) {
      attribution.initial_prompt = prompt.slice(0, MAX_VALUE_LENGTH);
    }

    writeAttributionCookie(attribution);
  } catch {
    // Attribution must never break app boot.
  }
}

/**
 * Landing-page prompt handoff: the marketing hero links here with
 * `?prompt=<idea>`. Stash it in the new-chat composer draft (localStorage,
 * survives the signup/login round-trip) so the composer arrives prefilled,
 * then strip the param from the URL. Call AFTER captureFirstTouchAttribution
 * so the prompt is still present for the attribution cookie.
 */
export function captureLandingPrompt(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const prompt = params.get(PROMPT_PARAM)?.trim();

    if (!prompt) {
      return;
    }

    setDraft(undefined, prompt);
    localStorage.setItem(AUTOSTART_KEY, String(Date.now()));

    params.delete(PROMPT_PARAM);

    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  } catch {
    // Prompt handoff must never break app boot.
  }
}

/*
 * The landing prompt is a build the visitor already asked for, so the home
 * composer starts it instead of waiting on a second press. The flag lives in
 * localStorage so it survives the magic-link round-trip (a new tab), and the
 * first real send clears it.
 */
const AUTOSTART_KEY = 'landing-prompt:autostart';
const AUTOSTART_TTL_MS = 60 * 60 * 1000;

export function hasLandingAutostart(): boolean {
  try {
    const at = Number(localStorage.getItem(AUTOSTART_KEY));

    return at > 0 && Date.now() - at < AUTOSTART_TTL_MS;
  } catch {
    return false;
  }
}

export function clearLandingAutostart(): void {
  try {
    localStorage.removeItem(AUTOSTART_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}
